/**
 * The right-hand pane: the answer first, then the one thing selected, then
 * what every ray did inside every block (optics-bench-target.md §Right).
 *
 * The answer leads because nearly every question in the chapter is the same
 * question — where is the image, and what is it like — and the five things
 * that answer it (position, size, real or virtual, erect or inverted,
 * magnified or diminished) belong together.
 *
 * The selection's controls come from its kind's `controls()`, as data, so a
 * new kind of part adds nothing here.
 */
import { useState } from 'react';
import { optics } from '@padcanvas/shared';
import type { optics as O } from '@padcanvas/shared';
import { WHITE_SWATCH, wavelengthInk } from './palette';

const { defOf, movePart, turnPart, setMount, setData, rad, deg, rayWorking, wavelengthOf, isWhite, WHITE_LEAD } = optics;

type World = O.World;
type Edit = (f: (w: World) => World) => void;

export function ResultsPane({ world, analysis, selectedId, onEdit, onRemove, claim, showSelection = true, theme }: {
    world: World;
    analysis: O.Analysis;
    selectedId: string | null;
    onEdit: Edit;
    onRemove: (id: string) => void;
    /** Off on a phone, where a part's settings sit under its tile in the parts
     *  pane: in a pane named Results, settings read as results. */
    showSelection?: boolean;
    theme?: 'light' | 'dark';
    /** The loaded preset's textbook claim — a result, so it is shown here beside
     *  the bench's own answer rather than in the setup pane. */
    claim?: { label: string; text: string };
}): React.JSX.Element {
    const selected = world.parts.find(p => p.id === selectedId);
    // The working is written for each ray box's ray — its middle one, when it
    // sends several, the same ray the angles are drawn on.
    // White light is written up once, for its green ray.
    const worked = analysis.rays.filter(r => r.role === 'single' && r.source.point === 'ray' && (!r.white || r.wavelength === WHITE_LEAD));
    return (
        <div className="obench-results">
            {/* What you adjust first; everything worked out from it below. */}
            {showSelection && selected && <Selection part={selected} world={world} onEdit={onEdit} onRemove={onRemove} theme={theme} />}

            {claim && (
                <section className="obench-textbook">
                    <h3 className="obench-panehead">The textbook says</h3>
                    <p className="obench-muted">{claim.label}</p>
                    <p className="obench-claim">{claim.text}</p>
                </section>
            )}
            {analysis.images.length > 0 && <h3 className="obench-panehead">The image</h3>}
            {analysis.images.length === 0 && analysis.crossings.length === 0 && (
                <p className="obench-muted">Put an object or a point source on the bench, and a lens or mirror in front of it, to see where the image forms.</p>
            )}
            {analysis.images.map(r => <AnswerCard key={r.partId} report={r} world={world} />)}

            {worked.map(ray => <Working key={ray.id} world={world} analysis={analysis} ray={ray} />)}

            {analysis.crossings.length > 0 && <Crossings world={world} crossings={analysis.crossings} rays={analysis.rays} theme={theme} />}
        </div>
    );
}

// ── the answer ───────────────────────────────────────────────────────────────

const f1 = (x: number): string => (Math.abs(x) < 0.05 ? '0.0' : x.toFixed(1));

function sizeWord(m: number): string {
    const a = Math.abs(m);
    if (a > 1.005) return `magnified ×${a.toFixed(2)}`;
    if (a < 0.995) return `diminished ×${a.toFixed(2)}`;
    return 'the same size';
}

function AnswerCard({ report, world }: { report: O.ImageReport; world: World }): React.JSX.Element {
    const part = world.parts.find(p => p.id === report.partId) as O.Part;
    const name = defOf(part).name(part, world);
    const lead = report.points.find(p => p.t === 1) ?? report.points[0];
    const last = report.stages[report.stages.length - 1];
    const isRod = part.kind === 'object' && (part.data as unknown as O.ObjectData).form === 'rod';

    // Across F the image is two pieces through infinity: one real, one virtual.
    // "Erect", "stretched" and a colour order mean nothing across that gap.
    const split = report.pieces.length >= 2;
    const ends = [report.points.find(p => p.t === 0), report.points.find(p => p.t === 1)];

    let headline: React.ReactNode;
    if (split) {
        headline = <b className="obench-nature">in two pieces — one real, one virtual</b>;
    } else if (lead.kind === 'real' || lead.kind === 'virtual') {
        // Erect or inverted, and how big, are the textbook's words for an UPRIGHT
        // object; a tilted one is described end by end below.
        const nature = [
            lead.kind,
            !report.transverse || report.erect === undefined ? null : report.erect ? 'erect' : 'inverted',
            report.magnification === undefined ? null : sizeWord(report.magnification),
        ].filter(Boolean).join(', ');
        headline = <b className="obench-nature" data-kind={lead.kind}>{nature}</b>;
    } else if (lead.kind === 'at-infinity') {
        headline = <b className="obench-nature">at infinity — the rays leave parallel</b>;
    } else {
        headline = <b className="obench-nature" data-kind="none">no image</b>;
    }

    return (
        <section className="obench-card">
            <header>Image of {name}</header>
            <p>{headline}</p>
            {split && (
                <p className="obench-muted">
                    {ends.map((e, i) => e && (
                        <span key={i}>End {i === 0 ? 'A' : 'B'} images {e.kind === 'real' || e.kind === 'virtual'
                            ? <>as a {e.kind} point at x = {f1(e.at.x)} cm</> : <>{e.kind === 'at-infinity' ? 'at infinity' : 'nowhere'}</>}.{' '}</span>
                    ))}
                    The part of the object at the focus images at infinity, which is the gap between the pieces.
                </p>
            )}
            {!split && (lead.kind === 'real' || lead.kind === 'virtual') && (
                <p className="obench-where">
                    {report.applies && last && Number.isFinite(last.v)
                        ? <>v = {last.v > 0 ? '+' : '−'}{f1(Math.abs(last.v))} cm from {last.name}</>
                        : <>at x = {f1(lead.at.x)} cm, y = {f1(lead.at.y)} cm</>}
                    {report.height !== undefined && <>; height {f1(report.height)} cm</>}
                </p>
            )}
            {lead.kind === 'none' && lead.why && <p className="obench-muted">{lead.why}</p>}
            {isRod && !split && report.transverse && report.erect === false && (
                <p className="obench-muted">Its colour order is reversed: end A is now above end B.</p>
            )}
            {!split && report.endMagnification && (
                <p className="obench-muted">
                    Tilted, so each end is magnified differently: end A ×{Math.abs(report.endMagnification.a).toFixed(2)},
                    end B ×{Math.abs(report.endMagnification.b).toFixed(2)}.
                    {report.longitudinal !== undefined && <> Along the axis the image is stretched ×{Math.abs(report.longitudinal).toFixed(2)} = m_A × m_B.</>}
                </p>
            )}
            {report.working.length > 0 && (
                <ol className="obench-working">
                    {report.working.map((w, i) => <li key={i}>{w}</li>)}
                </ol>
            )}
            {/* Why the formula does not apply — only worth saying when there is an image
                it could have described. With no image, the line above has said why. */}
            {!report.applies && report.why && lead.kind !== 'none' && <p className="obench-muted">{report.why}</p>}
        </section>
    );
}

// ── surface by surface ──────────────────────────────────────────────────────

/** Snell's law, the critical angle or the law of reflection at each face the ray meets. */
function Working({ world, analysis, ray }: { world: World; analysis: O.Analysis; ray: O.Ray }): React.JSX.Element | null {
    const work = rayWorking(world, analysis, ray);
    if (work.steps.length === 0) return null;
    return (
        <section className="obench-card obench-surfaces">
            <header>At each surface, for {work.source}'s {ray.white ? 'green ray (white light: red, green and violet)' : 'ray'}</header>
            <ol>
                {work.steps.map((s, i) => (
                    <li key={i}>
                        <b>{s.title}</b>
                        {s.lines.map((line, j) => <span key={j}>{line}</span>)}
                    </li>
                ))}
            </ol>
            {work.summary.length > 0 && (
                <div className="obench-summary">{work.summary.map((line, i) => <p key={i}>{line}</p>)}</div>
            )}
        </section>
    );
}

// ── the selection ────────────────────────────────────────────────────────────

export function Selection({ part, world, onEdit, onRemove, theme }: {
    /** The bench's theme, so a colour swatch matches the rays it draws. */
    theme?: 'light' | 'dark';
    part: O.Part;
    world: World;
    onEdit: Edit;
    onRemove: (id: string) => void;
}): React.JSX.Element {
    const kind = defOf(part);
    const name = kind.name(part, world);
    const facts = kind.keyFacts?.(part, world) ?? [];
    return (
        <section className="obench-selection">
            <div className="obench-selhead">
                <h3 className="obench-panehead">{name} <span className="obench-muted">· {kind.label}</span></h3>
                {/* At the top, in reach of a thumb without scrolling past every
                    setting first. Undo brings the part back. */}
                <button type="button" className="obench-textbtn obench-remove" onClick={() => onRemove(part.id)}
                    title={`Remove ${name}`}>Remove this part</button>
            </div>

            {kind.freedom.onAxis && (
                <div className="obench-seg" role="group" aria-label="Where it sits">
                    <button type="button" aria-pressed={part.mount === 'axis'} onClick={() => onEdit(w => setMount(w, part.id, 'axis'))}>On the axis</button>
                    <button type="button" aria-pressed={part.mount === 'free'} onClick={() => onEdit(w => setMount(w, part.id, 'free'))}>Lifted off</button>
                </div>
            )}

            <NumberRow label="Position x" unit="cm" value={part.at.x} step={0.5} min={world.axis.from} max={world.axis.to}
                onChange={x => onEdit(w => movePart(w, part.id, { x, y: part.at.y }))} />
            {part.mount === 'free' && (
                <NumberRow label="Height y" unit="cm" value={part.at.y} step={0.5} min={-200} max={200}
                    onChange={y => onEdit(w => movePart(w, part.id, { x: part.at.x, y }))} />
            )}
            {kind.freedom.turns && part.mount === 'free' && (
                <NumberRow label="Angle" unit="°" value={deg(part.angle)} step={1} min={-180} max={180}
                    onChange={a => onEdit(w => turnPart(w, part.id, rad(a)))} />
            )}
            {kind.freedom.turns && part.mount === 'axis' && (
                <button type="button" className="obench-button" onClick={() => onEdit(w => turnPart(w, part.id, part.angle + Math.PI))}>
                    Turn it round
                </button>
            )}

            {kind.controls(part, world).map(c => (
                c.type === 'choice'
                    ? (
                        <div key={c.key} className="obench-choice">
                            <span>{c.label}</span>
                            <div className="obench-seg">
                                {c.choices.map(ch => (
                                    <button key={ch.value} type="button" aria-pressed={part.data[c.key] === ch.value}
                                        onClick={() => onEdit(w => setData(w, part.id, { [c.key]: ch.value }))}>{ch.label}</button>
                                ))}
                            </div>
                        </div>
                    )
                    : c.type === 'wavelength'
                    ? (
                        <WavelengthRow key={c.key} label={c.label} value={wavelengthOf(part, world)} theme={theme}
                            white={c.white ? isWhite(part) : undefined}
                            onWhite={() => onEdit(w => setData(w, part.id, { white: true }))}
                            onChange={v => onEdit(w => setData(w, part.id, { [c.key]: v, ...(c.white ? { white: false } : {}) }))} />
                    )
                    : (
                        <ControlRow key={c.key} control={c} value={part.data[c.key] as number | null}
                            onChange={v => onEdit(w => setData(w, part.id, { [c.key]: v, ...(c.sets ?? {}) }))} />
                    )
            ))}

            {facts.length > 0 && (
                <dl className="obench-facts">
                    {facts.map(f => <div key={f.label}><dt>{f.label}</dt><dd>{f.value}</dd></div>)}
                </dl>
            )}

        </section>
    );
}

type NumberControl = Extract<O.Control, { type: 'number' }>;

/** The colours a student asks for by name, each at a wavelength a textbook
 *  would quote for it. Yellow is the sodium line every "n = 1.5" is given at. */
const NAMED_COLOURS: { name: string; nm: number }[] = [
    { name: 'Red', nm: 650 }, { name: 'Orange', nm: 600 }, { name: 'Yellow', nm: 589 },
    { name: 'Green', nm: 530 }, { name: 'Blue', nm: 470 }, { name: 'Violet', nm: 410 },
];

/** A source's colour: a named colour in one tap, or any wavelength exactly —
 *  or, on a source that may shine it (`white` defined), white light. */
function WavelengthRow({ label, value, onChange, theme, white, onWhite }: {
    label: string;
    theme?: 'light' | 'dark';
    value: number;
    onChange: (nm: number) => void;
    /** Whether it shines white now; undefined where white is not offered. */
    white?: boolean;
    onWhite: () => void;
}): React.JSX.Element {
    return (
        <div className="obench-control">
            <div className="obench-choice">
                <span>{label}</span>
                <div className="obench-seg obench-colours" role="group" aria-label={label}>
                    {NAMED_COLOURS.map(c => (
                        <button key={c.nm} type="button" aria-pressed={!white && Math.round(value) === c.nm} title={`${c.name}, ${c.nm} nm`}
                            onClick={() => onChange(c.nm)}>
                            <i style={{ background: wavelengthInk(c.nm, theme) }} aria-hidden="true" />{c.name}
                        </button>
                    ))}
                    {white !== undefined && (
                        <button type="button" aria-pressed={white} title="White: red, green and violet together" onClick={onWhite}>
                            <i style={{ background: WHITE_SWATCH }} aria-hidden="true" />White
                        </button>
                    )}
                </div>
            </div>
            {!white && <div className="obench-wave">
                <NumberRow label="Wavelength" unit="nm" value={value} min={400} max={700} step={1}
                    onChange={n => onChange(Math.round(n))} />
                <i style={{ background: wavelengthInk(value, theme) }} aria-hidden="true" />
            </div>}
        </div>
    );
}

function ControlRow({ control, value, onChange }: {
    control: NumberControl;
    value: number | null;
    onChange: (v: number | null) => void;
}): React.JSX.Element {
    if (control.nullLabel) return <RadiusRow control={control} value={value} onChange={onChange} />;
    return (
        <div className="obench-control">
            <NumberRow label={control.label} unit={control.unit} value={value ?? 0}
                min={control.min} max={control.max} step={control.step} onChange={onChange} />
        </div>
    );
}

// ── a radius: signed, and flat at either end ─────────────────────────────────

/** Slider positions: 0 to RADIUS_TRACK, the middle being the smallest radius. */
const RADIUS_TRACK = 1000;
/** Out from the middle, as a share of either half: linear to here… */
const LINEAR_END = 0.5;
/** …then exponential to here, and flat beyond. */
const FLAT_FROM = 0.95;
/** cm: where the linear stretch ends, and where the exponential one does. */
const LINEAR_CM = 50;
const LONG_CM = 500;

/** A slider position → a radius in cm, or null for flat. Pure: tested in __tests__. */
export function radiusAt(pos: number, minMagnitude: number): number | null {
    const u = (pos - RADIUS_TRACK / 2) / (RADIUS_TRACK / 2);
    const a = Math.min(1, Math.abs(u));
    const sign = u < 0 ? -1 : 1;
    if (a >= FLAT_FROM) return null;
    let r: number;
    if (a <= LINEAR_END) {
        r = minMagnitude + (LINEAR_CM - minMagnitude) * (a / LINEAR_END);
        r = Math.round(r * 2) / 2;
    } else {
        r = LINEAR_CM * Math.pow(LONG_CM / LINEAR_CM, (a - LINEAR_END) / (FLAT_FROM - LINEAR_END));
        const step = r < 100 ? 1 : 5;
        r = Math.round(r / step) * step;
    }
    return sign * r;
}

/** A radius (null: flat) → where the slider's thumb sits. A radius past the
 *  scale's long end sits just short of flat, which it is not — two whole steps
 *  short, so rounding the thumb to a step never tips it over. */
export function radiusPos(r: number | null, minMagnitude: number): number {
    const half = RADIUS_TRACK / 2;
    if (r === null) return RADIUS_TRACK;
    const m = Math.abs(r);
    const a = m <= LINEAR_CM
        ? LINEAR_END * Math.max(0, (m - minMagnitude) / (LINEAR_CM - minMagnitude))
        : Math.min(FLAT_FROM - 4 / RADIUS_TRACK, LINEAR_END + (FLAT_FROM - LINEAR_END) * Math.log10(m / LINEAR_CM) / Math.log10(LONG_CM / LINEAR_CM));
    return half + (r < 0 ? -1 : 1) * a * half;
}

/**
 * A lens face's radius, on a scale shaped like the job: the middle quarter of
 * each side is linear to 50 cm, where the shapes a student compares live;
 * beyond it the scale opens out exponentially to 5 m; and the last stretch at
 * either end is flat — a plano face is the limit of a long radius, so it sits
 * where a long radius goes, not behind a checkbox. Left of the middle is
 * negative, right positive, as the sign convention writes them.
 */
function RadiusRow({ control, value, onChange }: {
    control: NumberControl;
    value: number | null;
    onChange: (v: number | null) => void;
}): React.JSX.Element {
    const m = control.minMagnitude ?? 1;
    const [text, setText] = useState<string | null>(null);
    const shown = value === null ? '∞' : value.toFixed(1);
    const commit = (): void => {
        if (text === null) return;
        const t = text.trim().toLowerCase();
        if (t === '∞' || t === 'inf' || t === 'infinity' || t === control.nullLabel) onChange(null);
        else {
            const v = Number(t);
            // Typed values are exact — only a radius too small to be a lens is pushed out.
            if (Number.isFinite(v)) onChange(Math.abs(v) < m ? (v < 0 ? -m : m) : v);
        }
        setText(null);
    };
    return (
        <div className="obench-control">
            <label className="obench-number obench-radius" data-flat={value === null}>
                <span>{control.label}</span>
                <input
                    type="range" min={0} max={RADIUS_TRACK} step={1}
                    value={Math.round(radiusPos(value, m))}
                    aria-valuetext={value === null ? control.nullLabel : `${value.toFixed(1)} ${control.unit ?? ''}`.trim()}
                    onChange={e => onChange(radiusAt(+e.target.value, m))}
                />
                <input
                    type="text" inputMode="decimal" aria-label={`${control.label}${control.unit ? `, ${control.unit}` : ''} (∞ for ${control.nullLabel})`}
                    value={text ?? shown}
                    onChange={e => setText(e.target.value)}
                    onBlur={commit}
                    onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setText(null); }}
                />
                {control.unit && <em>{control.unit}</em>}
            </label>
        </div>
    );
}

/** A slider with a typed value beside it. The typed value is exact and never snapped. */
export function NumberRow({ label, unit, value, min, max, step, disabled, onChange }: {
    label: string;
    unit?: string;
    value: number;
    min: number;
    max: number;
    step: number;
    disabled?: boolean;
    onChange: (v: number) => void;
}): React.JSX.Element {
    const [text, setText] = useState<string | null>(null);
    const dp = step < 0.1 ? 2 : step < 1 ? 1 : 0;
    const commit = (): void => {
        if (text === null) return;
        const v = Number(text);
        if (Number.isFinite(v)) onChange(v);
        setText(null);
    };
    return (
        <label className="obench-number" data-disabled={disabled}>
            <span>{label}</span>
            <input
                type="range" min={min} max={max} step={step} value={Math.min(max, Math.max(min, value))}
                disabled={disabled} onChange={e => onChange(+e.target.value)}
            />
            <input
                type="text" inputMode="decimal" aria-label={`${label}${unit ? `, ${unit}` : ''}`}
                value={text ?? value.toFixed(dp)} disabled={disabled}
                onChange={e => setText(e.target.value)}
                onBlur={commit}
                onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setText(null); }}
            />
            {unit && <em>{unit}</em>}
        </label>
    );
}

// ── inside the blocks ────────────────────────────────────────────────────────

function Crossings({ world, crossings, rays, theme }: {
    world: World; crossings: O.Crossing[]; rays: O.Ray[]; theme?: 'light' | 'dark';
}): React.JSX.Element {
    const [open, setOpen] = useState(true);
    // White light's rows are its colours': each is marked with a dot of its own.
    const whiteNm = new Map(rays.filter(r => r.white).map(r => [r.id, r.wavelength]));
    const nameOf = (id: string): string => {
        const p = world.parts.find(x => x.id === id) as O.Part;
        return defOf(p).name(p, world);
    };
    return (
        <section className="obench-group" data-open={open}>
            <button type="button" className="obench-grouphead" aria-expanded={open} onClick={() => setOpen(o => !o)}>
                Through the blocks<span aria-hidden="true">⌃</span>
            </button>
            {open && (
                <table className="obench-table">
                    <thead>
                        <tr><th>Block</th><th>i</th><th>r</th><th>e</th><th>δ</th><th>shift</th></tr>
                    </thead>
                    <tbody>
                        {crossings.slice(0, 12).map((c, k) => (
                            <tr key={k}>
                                <td>
                                    {whiteNm.has(c.rayId) && <i className="obench-raydot" style={{ background: wavelengthInk(whiteNm.get(c.rayId) as number, theme) }} aria-hidden="true" />}
                                    {nameOf(c.partId)}{c.tir ? ' · TIR' : ''}
                                </td>
                                <td>{c.entry.incidence?.toFixed(1)}°</td>
                                <td>{c.entry.refraction !== undefined ? `${c.entry.refraction.toFixed(1)}°` : '—'}</td>
                                <td>{c.exit.refraction !== undefined ? `${c.exit.refraction.toFixed(1)}°` : '—'}</td>
                                <td>{c.deviation.toFixed(1)}°</td>
                                <td>{c.lateral !== undefined ? `${c.lateral.toFixed(2)} cm` : '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </section>
    );
}
